<script lang="ts">
  import Icon from "../Icon.svelte";

  interface Props {
    data: {
      hash: string;
      status: string;
      from: string;
      to: string;
      value: string;
      gasUsed: string;
      gasPrice: string;
      timestamp: number;
      block: number;
      method?: string;
    };
  }

  let { data }: Props = $props();

  let timeAgo = $derived.by(() => {
    const seconds = Math.floor(Date.now() / 1000 - data.timestamp);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  });
</script>

<div class="tx-result fade-in-up">
  <header class="result-header">
    <div class="result-icon" class:success={data.status === "Success"}>
      <Icon name="check" size={20} />
    </div>
    <div class="result-title">
      <div class="title-row">
        <h2>Transaction</h2>
        <span class="status-badge" class:success={data.status === "Success"}>
          {data.status}
        </span>
      </div>
      <span class="result-hash">{data.hash}</span>
    </div>
    <button class="copy-btn" title="Copy hash">
      <Icon name="copy" size={14} />
    </button>
  </header>

  <!-- From -> To -->
  <div class="transfer-flow">
    <div class="transfer-party">
      <span class="party-label">From</span>
      <span class="party-address">{data.from}</span>
    </div>
    <div class="transfer-arrow">
      <Icon name="arrow-right" size={20} />
    </div>
    <div class="transfer-party">
      <span class="party-label">To</span>
      <span class="party-address">{data.to}</span>
    </div>
  </div>

  <!-- Stats -->
  <div class="stats-grid">
    <div class="stat">
      <span class="stat-label">Value</span>
      <span class="stat-value">{data.value}</span>
    </div>
    <div class="stat">
      <span class="stat-label">Gas Used</span>
      <span class="stat-value">{data.gasUsed}</span>
    </div>
    <div class="stat">
      <span class="stat-label">Gas Price</span>
      <span class="stat-value">{data.gasPrice}</span>
    </div>
    <div class="stat">
      <span class="stat-label">Block</span>
      <span class="stat-value">#{data.block.toLocaleString()}</span>
    </div>
  </div>

  <div class="meta-row">
    <div class="meta-item">
      <Icon name="clock" size={14} />
      <span>{timeAgo}</span>
    </div>
    {#if data.method}
      <div class="meta-item">
        <Icon name="terminal" size={14} />
        <code>{data.method}()</code>
      </div>
    {/if}
  </div>

  <div class="actions">
    <button class="action-btn primary">
      <Icon name="repeat" size={16} />
      Simulate Again
    </button>
    <button class="action-btn">
      <Icon name="cube" size={16} />
      View Block
    </button>
    <button class="action-btn">
      <Icon name="external-link" size={16} />
      Etherscan
    </button>
  </div>
</div>

<style>
  .tx-result {
    background: var(--bg-secondary);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-lg);
    overflow: hidden;
  }

  .result-header {
    display: flex;
    align-items: flex-start;
    gap: var(--space-4);
    padding: var(--space-6);
    border-bottom: 1px solid var(--border-subtle);
  }

  .result-icon {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-tertiary);
    border-radius: var(--radius-full);
    color: var(--text-tertiary);
    flex-shrink: 0;
  }

  .result-icon.success {
    background: rgba(34, 197, 94, 0.1);
    color: var(--accent-success);
  }

  .result-title {
    flex: 1;
    min-width: 0;
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .title-row h2 {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  .status-badge {
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: var(--radius-full);
    background: var(--bg-tertiary);
    color: var(--text-secondary);
    text-transform: uppercase;
  }

  .status-badge.success {
    background: rgba(34, 197, 94, 0.1);
    color: var(--accent-success);
  }

  .result-hash {
    font-family: var(--font-mono);
    font-size: 0.8125rem;
    color: var(--text-tertiary);
    margin-top: var(--space-1);
    display: block;
  }

  .copy-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: var(--radius-sm);
    color: var(--text-tertiary);
    transition: all var(--transition-fast);
    flex-shrink: 0;
  }

  .copy-btn:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }

  .transfer-flow {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-5);
    background: var(--bg-tertiary);
  }

  .transfer-party {
    flex: 1;
    min-width: 0;
    padding: var(--space-4);
    background: var(--bg-secondary);
    border-radius: var(--radius-md);
  }

  .party-label {
    display: block;
    font-size: 0.6875rem;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: var(--space-1);
  }

  .party-address {
    font-family: var(--font-mono);
    font-size: 0.875rem;
    color: var(--text-primary);
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .transfer-arrow {
    color: var(--text-tertiary);
    flex-shrink: 0;
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--space-4);
    padding: var(--space-5);
  }

  @media (max-width: 640px) {
    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .stat-label {
    font-size: 0.6875rem;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .stat-value {
    font-size: 0.9375rem;
    font-weight: 600;
    font-family: var(--font-mono);
    color: var(--text-primary);
  }

  .meta-row {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-4) var(--space-5);
    border-top: 1px solid var(--border-subtle);
  }

  .meta-item {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: 0.8125rem;
    color: var(--text-secondary);
  }

  .meta-item code {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    padding: 2px 6px;
    background: var(--bg-tertiary);
    border-radius: var(--radius-sm);
  }

  .actions {
    display: flex;
    gap: var(--space-3);
    padding: var(--space-5);
    border-top: 1px solid var(--border-subtle);
  }

  .action-btn {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-md);
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-secondary);
    background: var(--bg-tertiary);
    border: 1px solid var(--border-subtle);
    transition: all var(--transition-fast);
  }

  .action-btn:hover {
    border-color: var(--border-default);
    color: var(--text-primary);
  }

  .action-btn.primary {
    background: var(--accent-primary);
    border-color: var(--accent-primary);
    color: white;
  }

  .action-btn.primary:hover {
    background: #2563eb;
  }
</style>
